import { useState } from "react";
import { ClipboardPlus } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const initialForm = {
  client_id: "",
  role_name: "",
  needed_date: "",
  hours: 40,
  employment_type: "Full-time",
  engagement_type: "Temporary",
  priority: "Standard",
  required_credentials: "",
  notes: "",
};

export const StaffingRequestForm = ({ clientOptions, onCancel, onCreated }) => {
  const [form, setForm] = useState(() => ({ ...initialForm, client_id: clientOptions.length === 1 ? clientOptions[0].id : "" }));
  const [error, setError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const updateField = (event) => setForm({ ...form, [event.target.name]: event.target.value });

  async function submit(event) {
    event.preventDefault();
    setIsSaving(true);
    setError("");
    try {
      const response = await api.post("/requests", {
        ...form,
        hours: Number(form.hours),
        required_credentials: form.required_credentials.split(",").map((item) => item.trim()).filter(Boolean),
      });
      onCreated(response.data);
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <section className="request-form-panel" data-testid="staffing-request-form">
      <div className="section-heading">
        <div>
          <p className="eyebrow">NEW REQUEST</p>
          <h2>Describe the staffing need</h2>
        </div>
        <button type="button" className="subtle-link" data-testid="cancel-request-button" onClick={onCancel}>Cancel</button>
      </div>
      {error && <p className="form-message error-message" data-testid="request-form-error">{error}</p>}
      <form onSubmit={submit} className="request-form-grid">
        <label className="form-full">
          Client
          <select data-testid="request-client-select" name="client_id" value={form.client_id} onChange={updateField} required>
            <option value="">Select a client</option>
            {clientOptions.map((client) => <option key={client.id} value={client.id}>{client.name}</option>)}
          </select>
        </label>
        <label>Role<input data-testid="request-role-input" name="role_name" value={form.role_name} onChange={updateField} required placeholder="e.g., Mathematics Teacher" /></label>
        <label>Date needed<input data-testid="request-date-input" name="needed_date" type="date" value={form.needed_date} onChange={updateField} required /></label>
        <label>Weekly hours<input data-testid="request-hours-input" name="hours" type="number" min="1" max="80" value={form.hours} onChange={updateField} required /></label>
        <label>Employment type
          <select data-testid="request-employment-input" name="employment_type" value={form.employment_type} onChange={updateField}>
            <option>Full-time</option>
            <option>Part-time</option>
          </select>
        </label>
        <label>Assignment type
          <select data-testid="request-engagement-input" name="engagement_type" value={form.engagement_type} onChange={updateField}>
            <option>Temporary</option>
            <option>Long-Term</option>
          </select>
        </label>
        <label>Priority
          <select data-testid="request-priority-input" name="priority" value={form.priority} onChange={updateField}>
            <option>Standard</option>
            <option>High</option>
            <option>Urgent</option>
          </select>
        </label>
        <label className="form-full">Required credentials<input data-testid="request-credentials-input" name="required_credentials" value={form.required_credentials} onChange={updateField} placeholder="Comma-separated credentials" /></label>
        <label className="form-full">Notes<textarea data-testid="request-notes-input" name="notes" value={form.notes} onChange={updateField} rows="3" placeholder="Add context your staffing team should know" /></label>
        <div className="form-full form-actions">
          <button className="button button-primary" data-testid="save-request-draft-button" type="submit" disabled={isSaving}>
            {isSaving ? "Saving…" : <><ClipboardPlus size={17} /> Save draft</>}
          </button>
        </div>
      </form>
    </section>
  );
};
